import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { AccountModeCard } from '../components/settings/AccountModeCard';
import { useMoney } from '../context/LocationContext';
import { toast } from 'sonner';
import { Loader2, Save, Settings as SettingsIcon, Wifi, Database, Shield, Clock, Sparkles } from 'lucide-react';

export default function Settings() {
  const { token } = useAuth();
  const { currencyFor } = useMoney();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agentSettings, setAgentSettings] = useState(null);
  const [savingAgents, setSavingAgents] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([api.getSettings(token), api.getAgentSettings(token)]);
      setSettings(s.data);
      setAgentSettings(a.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAgent = (k, v) => setAgentSettings((s) => ({ ...(s || {}), [k]: v }));
  const saveAgents = async () => {
    setSavingAgents(true);
    try {
      await api.saveAgentSettings(token, agentSettings);
      toast.success('AI agent settings saved');
    } catch (e) {
      toast.error('Failed to save agent settings');
    } finally {
      setSavingAgents(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSettings(token, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
            Settings
          </h1>
          <p className="text-slate-400 mt-1">Configure your sync preferences and API connections</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <AccountModeCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sync Configuration */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              Sync Configuration
            </CardTitle>
            <CardDescription className="text-slate-400">
              Configure how and when data syncs occur
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-slate-300">Sync Mode</Label>
              <Select 
                value={settings?.sync_mode || 'realtime'} 
                onValueChange={(v) => updateSetting('sync_mode', v)}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white" data-testid="sync-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="realtime">Real-time (Webhook)</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Sync Interval (minutes)</Label>
              <Select 
                value={String(settings?.sync_interval_minutes || 15)} 
                onValueChange={(v) => updateSetting('sync_interval_minutes', parseInt(v))}
                disabled={settings?.sync_mode === 'realtime'}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white" data-testid="sync-interval-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator className="bg-slate-800" />

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-300">Auto Retry</Label>
                <p className="text-slate-500 text-sm">Automatically retry failed syncs</p>
              </div>
              <Switch 
                checked={settings?.auto_retry || false}
                onCheckedChange={(v) => updateSetting('auto_retry', v)}
                data-testid="auto-retry-switch"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Max Retries</Label>
              <Select 
                value={String(settings?.max_retries || 3)} 
                onValueChange={(v) => updateSetting('max_retries', parseInt(v))}
                disabled={!settings?.auto_retry}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white" data-testid="max-retries-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="1">1 retry</SelectItem>
                  <SelectItem value="3">3 retries</SelectItem>
                  <SelectItem value="5">5 retries</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Sync Toggles */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-blue-400" />
              Sync Modules
            </CardTitle>
            <CardDescription className="text-slate-400">
              Enable or disable specific sync operations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30">
              <div>
                <Label className="text-slate-300">Sales Sync</Label>
                <p className="text-slate-500 text-xs">UnTill → Odoo invoices</p>
              </div>
              <Switch 
                checked={settings?.enable_sales_sync || false}
                onCheckedChange={(v) => updateSetting('enable_sales_sync', v)}
                data-testid="sales-sync-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30">
              <div>
                <Label className="text-slate-300">Product Sync</Label>
                <p className="text-slate-500 text-xs">Odoo → UnTill products</p>
              </div>
              <Switch 
                checked={settings?.enable_product_sync || false}
                onCheckedChange={(v) => updateSetting('enable_product_sync', v)}
                data-testid="product-sync-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30">
              <div>
                <Label className="text-slate-300">Customer Sync</Label>
                <p className="text-slate-500 text-xs">UnTill → Odoo customers</p>
              </div>
              <Switch 
                checked={settings?.enable_customer_sync || false}
                onCheckedChange={(v) => updateSetting('enable_customer_sync', v)}
                data-testid="customer-sync-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30">
              <div>
                <Label className="text-slate-300">Inventory Sync</Label>
                <p className="text-slate-500 text-xs">Stock level synchronization</p>
              </div>
              <Switch 
                checked={settings?.enable_inventory_sync || false}
                onCheckedChange={(v) => updateSetting('enable_inventory_sync', v)}
                data-testid="inventory-sync-switch"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30">
              <div>
                <Label className="text-slate-300">Accounting Sync</Label>
                <p className="text-slate-500 text-xs">Auto journal entries</p>
              </div>
              <Switch 
                checked={settings?.enable_accounting_sync || false}
                onCheckedChange={(v) => updateSetting('enable_accounting_sync', v)}
                data-testid="accounting-sync-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* UnTill API Configuration */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
              <Wifi className="w-5 h-5 text-blue-400" />
              UnTill POS Configuration
            </CardTitle>
            <CardDescription className="text-slate-400">
              Configure UnTill POS API connection (mock data for demo)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">API Key</Label>
              <Input
                type="password"
                placeholder="Enter UnTill API key"
                value={settings?.utill_api_key || ''}
                onChange={(e) => updateSetting('utill_api_key', e.target.value)}
                className="bg-slate-800/50 border-slate-700 text-white"
                data-testid="utill-api-key-input"
              />
              <p className="text-slate-500 text-xs">Demo mode: API key not required</p>
            </div>
          </CardContent>
        </Card>

        {/* Odoo Configuration */}
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-emerald-400" />
              Odoo ERP Configuration
            </CardTitle>
            <CardDescription className="text-slate-400">
              Configure Odoo connection (mock data for demo)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Odoo URL</Label>
              <Input
                placeholder="https://your-odoo-instance.com"
                value={settings?.odoo_url || ''}
                onChange={(e) => updateSetting('odoo_url', e.target.value)}
                className="bg-slate-800/50 border-slate-700 text-white"
                data-testid="odoo-url-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Database Name</Label>
              <Input
                placeholder="odoo_production"
                value={settings?.odoo_database || ''}
                onChange={(e) => updateSetting('odoo_database', e.target.value)}
                className="bg-slate-800/50 border-slate-700 text-white"
                data-testid="odoo-database-input"
              />
              <p className="text-slate-500 text-xs">Demo mode: Connection not required</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Autonomy — merchant-configurable per §4 spec */}
      <Card
        className="bg-gradient-to-br from-purple-500/[0.04] via-slate-900/60 to-slate-900/40 border-purple-500/20"
        data-testid="agent-autonomy-card"
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-300" />
                AI Agent Autonomy
              </CardTitle>
              <CardDescription className="text-slate-400">
                Control how much the AI agents can do on their own. Defaults are conservative — nothing auto-applies unless you turn it on.
              </CardDescription>
            </div>
            <Button
              onClick={saveAgents}
              disabled={savingAgents || !agentSettings}
              className="bg-purple-500 hover:bg-purple-600 text-white"
              data-testid="save-agent-settings-btn"
            >
              {savingAgents ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save autonomy settings
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-200">Auto-apply high-confidence actions</Label>
                  <p className="text-xs text-slate-500 mt-1">Master switch. When off, every AI suggestion needs a human tap.</p>
                </div>
                <Switch
                  checked={!!agentSettings?.auto_apply_enabled}
                  onCheckedChange={(v) => updateAgent('auto_apply_enabled', v)}
                  data-testid="autonomy-auto-apply"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">Confidence threshold</Label>
                <Input
                  type="number" step="0.01" min="0" max="1"
                  value={agentSettings?.auto_apply_confidence_threshold ?? 0.9}
                  onChange={(e) => updateAgent('auto_apply_confidence_threshold', parseFloat(e.target.value))}
                  className="bg-slate-800/50 border-slate-700 text-white font-mono"
                  data-testid="autonomy-threshold"
                />
                <p className="text-xs text-slate-500">AI must be at least this confident to auto-apply.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">High-value item threshold ({currencyFor()})</Label>
                <Input
                  type="number" step="1" min="0"
                  value={agentSettings?.high_value_threshold ?? 100}
                  onChange={(e) => updateAgent('high_value_threshold', parseFloat(e.target.value))}
                  className="bg-slate-800/50 border-slate-700 text-white font-mono"
                  data-testid="autonomy-high-value"
                />
                <p className="text-xs text-slate-500">Items priced above this always require human review, even when confident.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="text-xs uppercase tracking-wider text-slate-500">Per-agent kill switches</div>
              {[
                ['a1_alert_triage_enabled', 'A1 · Inventory Alert Triage'],
                ['a2_log_assistant_enabled', 'A2 · Log Assistant (Q&A)'],
                ['a3_mapping_enabled', 'A3 · Product / Account Mapping'],
              ].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between p-3 rounded-lg border border-slate-800 bg-slate-900/60">
                  <span className="text-slate-200 text-sm">{label}</span>
                  <Switch
                    checked={!!agentSettings?.[k]}
                    onCheckedChange={(v) => updateAgent(k, v)}
                    data-testid={`autonomy-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
